import {DoppeAllSupportedPremiumFeatures, DoppeAllSupportedPremiumFeaturesSpec} from './doppe-premium.auto-generated';
import type {Values} from '@wix/devzai-utils-common';
import {arrayGroupBy, enumReduce, evaluateFunction, iterableMapToArray, objectOmit} from '@wix/devzai-utils-common';
import type {DoppeDtoPremiumFeatureCatalogInfo} from '../types';
import type {DoppePremiumBooleanFeatures, DoppePremiumFeaturesStatus, DoppePremiumQuotaFeatures} from './doppe-premium';
import {doppePremiumFeatureHasFiniteRemainedQuota, doppePremiumFeatureIsDisabled} from './doppe-premium';
import {DoppeSupportedWixTpas} from '../types/doppe-supported-wix-tpas';
import {DoppePremiumImplementedPremiumFeatures} from "./doppe-premium-implemented-premium-features";

export const DoppeFreeToolsLimit = {
    LandingPages: 1,
    PreRolls: 0,
    ShortLinks: 0,
}

export const DoppePremiumFeatures = enumReduce(DoppeAllSupportedPremiumFeatures, DoppePremiumImplementedPremiumFeatures);
export type DoppePremiumFeatures = Values<typeof DoppePremiumFeatures>;

export const DoppePremiumFeaturesAlias = {
    editSocialShareSettings: DoppePremiumFeatures.hopp_social_share_settings_editing,
    importLinks: DoppePremiumFeatures.hopp_advanced_design_options,
    bannerAction: DoppePremiumFeatures.hopp_change_link_destination,
}

/**
 * All these features are blocking publishing.
 */
export const DoppePagePremiumFeatures = {
    RemoveBanner: 'RemoveBanner',
    VerifiedBadge: 'VerifiedBadge',
    ProLayout: 'ProLayout',
    ProListLayout: 'ProListLayout',
    LatestItemsInLists: 'LatestItemsInLists',
    LatestBlogPostsInLists: 'LatestBlogPostsInLists',
    LatestProductsInLists: 'LatestProductsInLists',
    UpcomingEventsInLists: 'UpcomingEventsInLists',
    LatestBookingServicesInLists: 'LatestBookingServicesInLists',
    PageListItemsLimit: 'PageListItemsLimit',
    ProButtonStyle: 'ProButtonStyle',
    ProProfilePictureShape: 'ProProfilePictureShape',
    ProActionDisplay: 'ProActionDisplay',
    ProFont: 'ProFont',
    ConnectToContactsList: 'ConnectToContactsList',
    AdvancedSearchSettings: 'AdvancedSearchSettings',
    ShowAsPopup: 'ShowAsPopup',
    ShowAsBanner: 'ShowAsBanner',
    ShowOnPage: 'ShowOnPage',
    SeoSettings: 'SeoSettings',
    SocialShare: 'SocialShare',
    ShowListItemAsPopup: 'ShowListItemAsPopup',
    FormActions: 'FormActions',
    PaymentActions: 'PaymentActions',
    BannerAction: 'BannerAction',
    LockSettings: 'LockSettings',
} as const;

export type DoppePagePremiumFeatures = Values<typeof DoppePagePremiumFeatures>;


export const DoppePageNoneViolatedPremiumFeatures = {
    ListItemsLimit: 'ListItemsLimit',
    SearchItemsLimit: 'SearchItemsLimit',
} as const;

export type DoppePageNoneViolatedPremiumFeatures = Values<typeof DoppePageNoneViolatedPremiumFeatures>;

export const DoppeBehaviourPremiumFeatures = {
    EditLandingPage: 'EditLandingPage',
    ManageLandingPage: 'ManageLandingPage',
    DuplicateLandingPage: 'DuplicateLandingPage',
    CreateLandingPages: 'CreateLandingPages',
    EditPreRoll: 'EditPreRoll',
    DuplicatePreRoll: 'DuplicatePreRoll',
    ManagePreRoll: 'ManagePreRoll',
    CreatePreRolls: 'CreatePreRolls',
    CreateShortLinks: 'CreateShortLinks',
    EditForm: 'EditForm',
    ManageForms: 'ManageForms',
    DuplicateForm: 'DuplicateForm',
    CreateForms: 'CreateForms',
    InviteCollaborators: 'InviteCollaborators',
    ManageShortLinks: 'ManageShortLinks',
    EditShortLinks: 'EditShortLinks',
    ImportShortLinks: 'ImportShortLinks',
    ImportShortLinksFromYoutube: 'ImportShortLinksFromYoutube',
    EditSocialShare: 'EditSocialShare',
    EditSeoSettings: 'EditSeoSettings',
    EditFaviconSettings: 'EditFaviconSettings',
    CreateNewSubscribeList: 'CreateNewSubscribeList',
    ExportContacts: 'ExportContacts',
    ConnectCRM: 'ConnectCRM',
    UpgradeCRM: 'UpgradeCRM',
    SocialLinksAnalytics: 'SocialLinksAnalytics',
    GoogleAnalytics: 'GoogleAnalytics',
    ConnectDomain: 'ConnectDomain',
    ConnectSubdomain: 'ConnectSubdomain',
    FreeDomainFor1Year: 'FreeDomainFor1Year',
    MediaStorage: 'MediaStorage',
    ChatSupport: 'ChatSupport',
    VIPSupport: 'VIPSupport',
    InstallHoppStoreApp: 'InstallHoppStoreApp',
    InstallHoppBookingServices: 'InstallHoppBookingServices',
    InstallHoppEventsApp: 'InstallHoppEventsApp',
    InstallHoppBlog: 'InstallHoppBlog',
    InstallHoppPricingPlans: 'InstallHoppPricingPlans',
    InstallPayments: 'InstallPayments',
    InstallAutomations: 'InstallAutomations',
    CustomThankYouAndRedirects: 'CustomThankYouAndRedirects',
    AdvancedCRM: 'AdvancedCRM',
    LeadMagnets: 'LeadMagnets',
    AutomationsCRM: 'AutomationsCRM',
    AcceptManuelOrders: 'AcceptManuelOrders',
    RecurringPayments: 'RecurringPayments',
    DigitalFiles: 'DigitalFiles',
    ManageProductsCatalog: 'ManageProductsCatalog',
    Dropshipping: 'Dropshipping',
    PrintOnDemand: 'PrintOnDemand',
    BookingVideoConferencing: 'BookingVideoConferencing',
    BookingSMSNotifications: 'BookingSMSNotifications',
    BookingPersonalCalenderSync: 'BookingPersonalCalenderSync',
    BookingChargeDepositesForServices: 'BookingChargeDepositesForServices',
    BookingStaffManagement: 'BookingStaffManagement',
    BasicEmailMarketing: 'BasicEmailMarketing',
    BasicSocialPostsManagement: 'BasicSocialPostsManagement',
    Analytics: 'Analytics',
    SalesAnalytics: 'SalesAnalytics',
} as const;

export type DoppeBehaviourPremiumFeatures = Values<typeof DoppeBehaviourPremiumFeatures>;


export const DoppeUpgradeRequiringPagePremiumFeatures = {
    ...objectOmit(DoppePagePremiumFeatures, [DoppePagePremiumFeatures.SeoSettings, DoppePagePremiumFeatures.LockSettings, DoppePagePremiumFeatures.VerifiedBadge]),
    ...DoppePageNoneViolatedPremiumFeatures,
} as const;

export type DoppeUpgradeRequiringPagePremiumFeatures = Values<typeof DoppeUpgradeRequiringPagePremiumFeatures>;

export const DoppeUpgradeRequiringPremiumFeatures = {
    ...DoppeUpgradeRequiringPagePremiumFeatures,
    [DoppePagePremiumFeatures.SeoSettings]: DoppePagePremiumFeatures.SeoSettings,
    [DoppePagePremiumFeatures.LockSettings]: DoppePagePremiumFeatures.LockSettings,
    [DoppePagePremiumFeatures.VerifiedBadge]: DoppePagePremiumFeatures.VerifiedBadge,
    ...DoppeBehaviourPremiumFeatures
} as const;

export type DoppeUpgradeRequiringPremiumFeatures = Values<typeof DoppeUpgradeRequiringPremiumFeatures>;


export function doppePremiumFeaturesGetPremiumFeatureByWixTpaId(wixTpaId: DoppeSupportedWixTpas) {
    switch (wixTpaId) {
        // StoresNew install is not premium-gated; selling is gated separately by InstallPayments.
        case DoppeSupportedWixTpas.StoresNew: return null;
        case DoppeSupportedWixTpas.Booking: return DoppeBehaviourPremiumFeatures.InstallHoppBookingServices;
        case DoppeSupportedWixTpas.Events: return DoppeBehaviourPremiumFeatures.InstallHoppEventsApp;
        case DoppeSupportedWixTpas.Blog: return DoppeBehaviourPremiumFeatures.InstallHoppBlog;
        case DoppeSupportedWixTpas.PricingPlans: return DoppeBehaviourPremiumFeatures.InstallHoppPricingPlans;
        case DoppeSupportedWixTpas.Stores: return DoppeBehaviourPremiumFeatures.InstallPayments;

        default: return null;
    }
}

export function doppePremiumFeatureIsAnyTypePremiumFeatureDisabled(premiumFeature: DoppeUpgradeRequiringPremiumFeatures, premiumFeaturesStatus: DoppePremiumFeaturesStatus | null): boolean {
    const featureName = getPremiumFeatureNameFromDoppePremiumFeature(premiumFeature);

    if (DoppeAllSupportedPremiumFeaturesSpec[featureName] === 'quota') {
        return doppePremiumFeatureHasFiniteRemainedQuota(premiumFeaturesStatus, featureName as DoppePremiumQuotaFeatures);
    }
    else {
        return doppePremiumFeatureIsDisabled(premiumFeaturesStatus, featureName as DoppePremiumBooleanFeatures);
    }
}

export function getPremiumFeatureNameFromDoppePremiumFeature(premiumFeature: DoppeUpgradeRequiringPremiumFeatures) : DoppePremiumFeatures   {

    const relatedFeature = evaluateFunction(() => {
        switch (premiumFeature) {
            case DoppePageNoneViolatedPremiumFeatures.SearchItemsLimit: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_indexed_links_in_search
            }
            case DoppePagePremiumFeatures.AdvancedSearchSettings: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.creators_advanced_search_settings
            }
            case DoppeBehaviourPremiumFeatures.ExportContacts:
            case DoppePagePremiumFeatures.ConnectToContactsList:
            case DoppeBehaviourPremiumFeatures.CreateNewSubscribeList:
            case DoppeBehaviourPremiumFeatures.UpgradeCRM:
            case DoppeBehaviourPremiumFeatures.ConnectCRM: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_contact_management
            }
            case DoppeBehaviourPremiumFeatures.ConnectDomain: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.connect_your_domain
            }
            case DoppeBehaviourPremiumFeatures.ConnectSubdomain: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.connect_your_domain
            }
            case DoppeBehaviourPremiumFeatures.FreeDomainFor1Year: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.connect_your_domain
            }
            case DoppeBehaviourPremiumFeatures.ManageLandingPage:
            case DoppeBehaviourPremiumFeatures.EditLandingPage:
            case DoppeBehaviourPremiumFeatures.DuplicateLandingPage:
            case DoppeBehaviourPremiumFeatures.CreateLandingPages: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_unlimited_pages,
            }
            case DoppeBehaviourPremiumFeatures.ManagePreRoll:
            case DoppeBehaviourPremiumFeatures.EditPreRoll:
            case DoppeBehaviourPremiumFeatures.DuplicatePreRoll:
            case DoppeBehaviourPremiumFeatures.CreatePreRolls: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.creators_pre_rolls_count,
            }
            case DoppeBehaviourPremiumFeatures.ManageForms:
            case DoppeBehaviourPremiumFeatures.EditForm:
            case DoppeBehaviourPremiumFeatures.DuplicateForm:
            case DoppeBehaviourPremiumFeatures.CreateForms: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.customizable_forms,
            }
            case DoppeBehaviourPremiumFeatures.InviteCollaborators: {
                return {
                    uniqueName: DoppeAllSupportedPremiumFeatures.identity_collaborators,
                }
            }
            case DoppeBehaviourPremiumFeatures.ManageShortLinks:
            case DoppeBehaviourPremiumFeatures.EditShortLinks:
            case DoppeBehaviourPremiumFeatures.CreateShortLinks: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_create_short_links,
            }
            case DoppeBehaviourPremiumFeatures.ImportShortLinks: return {
                // uniqueName: DoppeAllSupportedPremiumFeatures.hopp_import_links_from_youtube
                uniqueName: DoppePremiumFeaturesAlias.importLinks
            }
            case DoppeBehaviourPremiumFeatures.ImportShortLinksFromYoutube: return {
                // uniqueName: DoppeAllSupportedPremiumFeatures.hopp_import_links_from_youtube
                uniqueName: DoppePremiumFeaturesAlias.importLinks
            }
            case DoppeBehaviourPremiumFeatures.EditFaviconSettings: return {
                // uniqueName: DoppeAllSupportedPremiumFeatures.creators_favicon_
                uniqueName: DoppePremiumFeatures.creators_favicon_
            }
            case DoppePagePremiumFeatures.SeoSettings:
            case DoppeBehaviourPremiumFeatures.EditSeoSettings: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_seo_settings
            }
            case DoppePagePremiumFeatures.SocialShare:
            case DoppeBehaviourPremiumFeatures.EditSocialShare: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_social_share_settings_editing
            }
            case DoppeBehaviourPremiumFeatures.GoogleAnalytics: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_google_analytics
            }
            case DoppePagePremiumFeatures.LatestBlogPostsInLists:
            case DoppePagePremiumFeatures.LatestProductsInLists:
            case DoppePagePremiumFeatures.UpcomingEventsInLists:
            case DoppePagePremiumFeatures.LatestBookingServicesInLists:
            case DoppePagePremiumFeatures.LatestItemsInLists: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_latest_items_action
            }
            case DoppePageNoneViolatedPremiumFeatures.ListItemsLimit:
            case DoppePagePremiumFeatures.PageListItemsLimit: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_add_items_to_list_action
            }
            case DoppePagePremiumFeatures.BannerAction: return {
                uniqueName: DoppePremiumFeaturesAlias.bannerAction
            }
            case DoppePagePremiumFeatures.ShowAsBanner:
            case DoppePagePremiumFeatures.ShowOnPage:
            case DoppePagePremiumFeatures.ShowListItemAsPopup:
            case DoppePagePremiumFeatures.ProFont:
            case DoppePagePremiumFeatures.ProActionDisplay:
            case DoppePagePremiumFeatures.ProProfilePictureShape:
            case DoppePagePremiumFeatures.ProButtonStyle: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_advanced_design_options
            }
            case DoppePagePremiumFeatures.ProLayout: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_advanced_page_presets
            }
            case DoppePagePremiumFeatures.ShowAsPopup: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_full_screen_view
            }
            case DoppePagePremiumFeatures.ProListLayout: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_list_premium_styles
            }
            case DoppePagePremiumFeatures.RemoveBanner: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_remove_banner
            }
            case DoppeBehaviourPremiumFeatures.SocialLinksAnalytics: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_full_analytics
            }
            case DoppePagePremiumFeatures.VerifiedBadge: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_verified_badge
            }
            case DoppePagePremiumFeatures.FormActions: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.creators_form_submission
            }
            case DoppePagePremiumFeatures.PaymentActions: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.creators_monetization_actions
            }
            case DoppePagePremiumFeatures.LockSettings: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_lock_action
            }
            case DoppeBehaviourPremiumFeatures.MediaStorage: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.media_storage_total
            }
            case DoppeBehaviourPremiumFeatures.VIPSupport: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.vip_support
            }
            case DoppeBehaviourPremiumFeatures.ChatSupport: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_remove_banner
            }
            case DoppeBehaviourPremiumFeatures.CustomThankYouAndRedirects:
            case DoppeBehaviourPremiumFeatures.InstallHoppStoreApp:
            case DoppeBehaviourPremiumFeatures.InstallHoppEventsApp:
            case DoppeBehaviourPremiumFeatures.InstallHoppBookingServices:
            case DoppeBehaviourPremiumFeatures.InstallHoppPricingPlans: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_install_business_tpa_on_hopp_meta_s
            }
            case DoppeBehaviourPremiumFeatures.InstallPayments: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_connect_payments_by_wix_on_hopp_met
            }
            case DoppeBehaviourPremiumFeatures.InstallHoppBlog: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_enable_blog
            }
            case DoppeBehaviourPremiumFeatures.InstallAutomations: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_automations
            }

            case DoppeBehaviourPremiumFeatures.AdvancedCRM: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_contact_management
            }
            case DoppeBehaviourPremiumFeatures.LeadMagnets: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_lock_action
            }
            case DoppeBehaviourPremiumFeatures.AutomationsCRM: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_automations
            }
            case DoppeBehaviourPremiumFeatures.AcceptManuelOrders: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_install_business_tpa_on_hopp_meta_s
            }
            case DoppeBehaviourPremiumFeatures.RecurringPayments: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_connect_payments_by_wix_on_hopp_met
            }
            case DoppeBehaviourPremiumFeatures.DigitalFiles: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_install_business_tpa_on_hopp_meta_s
            }
            case DoppeBehaviourPremiumFeatures.ManageProductsCatalog: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_install_business_tpa_on_hopp_meta_s
            }
            case DoppeBehaviourPremiumFeatures.Dropshipping: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_install_business_tpa_on_hopp_meta_s
            }
            case DoppeBehaviourPremiumFeatures.PrintOnDemand: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_install_business_tpa_on_hopp_meta_s
            }
            case DoppeBehaviourPremiumFeatures.BookingVideoConferencing: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_install_business_tpa_on_hopp_meta_s
            }
            case DoppeBehaviourPremiumFeatures.BookingSMSNotifications: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.bookings_sms_reminders
            }
            case DoppeBehaviourPremiumFeatures.BookingPersonalCalenderSync: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_install_business_tpa_on_hopp_meta_s
            }
            case DoppeBehaviourPremiumFeatures.BookingChargeDepositesForServices: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_install_business_tpa_on_hopp_meta_s
            }
            case DoppeBehaviourPremiumFeatures.BookingStaffManagement: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.bookings_staff_limit
            }
            case DoppeBehaviourPremiumFeatures.BasicEmailMarketing: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_automations
            }
            case DoppeBehaviourPremiumFeatures.BasicSocialPostsManagement: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_automations
            }
            case DoppeBehaviourPremiumFeatures.SalesAnalytics: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_connect_payments_by_wix_on_hopp_met
            }
            case DoppeBehaviourPremiumFeatures.Analytics: return {
                uniqueName: DoppeAllSupportedPremiumFeatures.hopp_full_analytics
            }
        }
    })

    return relatedFeature.uniqueName;
}

export function doppeUpgradeRequiringPremiumFeaturesGroupByRelatedWixPremiumFeature (
    upgradeRequiringPremiumFeatures: DoppeUpgradeRequiringPremiumFeatures[]
) {
    return iterableMapToArray(
        arrayGroupBy(
            upgradeRequiringPremiumFeatures.map(upgradeRequiringFeature => {
                return {
                    upgradeRequiringFeature: upgradeRequiringFeature,
                    wixPremiumFeature: getPremiumFeatureNameFromDoppePremiumFeature(upgradeRequiringFeature)
                }
            }),
            item => item.wixPremiumFeature,
            item => item.upgradeRequiringFeature
        ),
        ([wixPremiumFeature, upgradeRequiringFeatures]) => {
            return {
                wixPremiumFeature: wixPremiumFeature,
                upgradeRequiringFeatures: upgradeRequiringFeatures
            }
        }
    )
}

export function convertPremiumFeaturetoPremiumFeatureCatalogInfo(premiumFeature: DoppeAllSupportedPremiumFeatures , quota?: number) : DoppeDtoPremiumFeatureCatalogInfo   {

    const limit = {
        low: quota ?? 0,
        high: 0,
        unsigned: false,
    }

    return {
        type: DoppeAllSupportedPremiumFeaturesSpec[premiumFeature],
        uniqueName: premiumFeature,
        limit: limit
    }
}


export function getPremiumFeatureFromDoppePremiumFeature(premiumFeature: DoppeUpgradeRequiringPremiumFeatures , quota?: number) : DoppeDtoPremiumFeatureCatalogInfo {
    return convertPremiumFeaturetoPremiumFeatureCatalogInfo(getPremiumFeatureNameFromDoppePremiumFeature(premiumFeature), quota);
}

export function isPremiumActionFeature(premiumFeature: DoppePagePremiumFeatures): boolean {
    return premiumFeature === DoppePagePremiumFeatures.PaymentActions || premiumFeature === DoppePagePremiumFeatures.FormActions;
}


export const DoppeAppUpgradeActionStatusContext = {
    Latest: 'Latest',
    ListLayout: 'ListLayout',
    ListItemsLimit: 'ListItemsLimit',
    ConnectToContactsList: 'ConnectToContactsList',
    PremiumFormAction: 'PremiumFormAction',
    PremiumPaymentAction: 'PremiumPaymentAction',
    PremiumBannerAction: 'PremiumBannerAction',
} as const;

export type DoppeAppUpgradeActionStatusContext = Values<typeof DoppeAppUpgradeActionStatusContext>;

export type DoppeAppUpgradeActionStatusContextStatus = {
    context: DoppeAppUpgradeActionStatusContext;
    isActive: boolean,
}